import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

interface QuizCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  color: string;
  questionCount: number;
  attempts: number;
  doneAt?: Date;
  href: string;
}

export default function QuizCard({
  title,
  description,
  icon,
  color,
  href,
  doneAt,
  questionCount,
  attempts = 0,
}: QuizCardProps) {
  return (
    <CardWrapper color={color}>
      <TopSection>
        <IconWrapper>{icon}</IconWrapper>
        <Content>
          <Title>{title}</Title>
          <Description>{description}</Description>
        </Content>
      </TopSection>
      {doneAt && (
        <DoneAt>
          Concluído em:{' '}
          {doneAt.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </DoneAt>
      )}
      <BottomSection>
        <DetailsContainer>
          <QuestionCount>{questionCount} Questões</QuestionCount>
          <QuizAttempts>{attempts}/2 Tentativas</QuizAttempts>
        </DetailsContainer>
        {attempts < 2 ? (
          <StartLink href={href}>Iniciar Quiz</StartLink>
        ) : (
          <StartLink href={`${href}?sa=true`}>Ver Respostas</StartLink>
        )}
      </BottomSection>
    </CardWrapper>
  );
}

const CardWrapper = styled.div<{ color: string }>`
  border-radius: 1rem;
  margin-top: 2.5rem;
  border-width: 4px;
  width: 100%; /* Ajusta a largura para ocupar o espaço disponível */
  max-width: 35rem; /* Define o limite máximo para o card */
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  ${({ color }) => css`
    background-color: ${color};
  `}

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TopSection = styled.div`
  display: flex;
  align-items: start;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const IconWrapper = styled.div`
  background-color: #ffffff;
  border-radius: 50%;
  padding: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Content = styled.div``;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: bold;
  color: #1f2937;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  color: #4b5563;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    text-align: center;
  }
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const QuestionCount = styled.span`
  background-color: #ffffff;
  border-radius: 9999px;
  padding: 0.25rem 1rem;
  font-size: 0.65rem;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.25rem 0.8rem;
  }
`;

const QuizAttempts = styled(QuestionCount)``;

const DoneAt = styled.span`
  font-size: 12px;
  color: #4b5563;
`;

const StartLink = styled.a`
  background-color: #7c3aed;
  color: #ffffff;
  font-weight: bold;
  border-radius: 1rem;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  text-decoration: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: #6d28d9;
  }

  @media (max-width: 768px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.9rem;
  }
`;
